import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useState } from 'react'
import PicklistField from 'core/components/validatedForm/DropdownField'
import Info from 'core/components/validatedForm/Info'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { waitSeconds } from 'openstack/components/infrastructure/blueprint/helpers'
import { updateFloatingIp } from 'openstack/components/networks/floating-ips/actions'
import UsedFloatingIpsPicklist from 'openstack/components/networks/floating-ips/UsedFloatingIpsPicklist'
import { getCurrentUser } from 'openstack/helpers'
import { refreshVirtualMachine } from './actions'
import { hasOtherUserVm } from './helpers'

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    gap: 16,
    display: 'grid',
  },
}))

export default function RemoveFloatingIpDialog({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const classes = useStyles()
  const [submitting, setSubmitting] = useState(false)

  const defaultParams = {
    floatingIp: '',
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const { update, updating, error, reset } = useUpdateAction(updateFloatingIp)
  const { update: refreshVm, updating: refreshingVm } = useUpdateAction(refreshVirtualMachine)

  const handleSubmit = useCallback(() => {
    setSubmitting(true)
    const asyncCalls = async () => {
      const body = {
        floatingip: {
          port_id: null,
        },
      }
      const { success } = await update({ id: params.floatingIp, body })
      await waitSeconds(3) // wait 3 seconds for VM private IP to be updated first
      await refreshVm({ id: vm.id })
      setSubmitting(false)
      if (success) handleClose(true)
    }
    asyncCalls()
  }, [params, vm])

  const handleClose = (success?: boolean) => {
    setParams(defaultParams)
    reset()
    onClose(success)
  }

  const { role: userRole, userId } = getCurrentUser()

  return (
    <ModalForm
      title="Remove Public IP"
      onSubmit={handleSubmit}
      onClose={onClose}
      loading={submitting}
      loadingMessage="Removing Public IP"
      submitting={submitting}
      error={error}
      submitTitle={`Remove Public IP`}
      panel="dialog"
      // maxWidth={1200}
      open
    >
      <div className={classes.fields}>
        {userRole === 'member' && hasOtherUserVm({ vms, userId }) && (
          <Info error>NOTE: You are performing an operation on another user's VM.</Info>
        )}
        <Text variant="body2">Select a public IP to remove from the selected VM.</Text>
        <PicklistField
          DropdownComponent={UsedFloatingIpsPicklist}
          id="floatingIp"
          label="Public IP to Remove"
          tooltip="List of public IPs attached to the selected VM"
          onChange={getParamsUpdater('floatingIp')}
          value={params.floatingIp}
          vmId={vm?.id}
          required
        />
      </div>
    </ModalForm>
  )
}
